import { useEffect, useState } from 'react';
import styled from "styled-components";
import { colors } from 'src/common/colors';
import { 
    Table, Thead, Tbody, Tr, Th, Td, TableContainer, 
    Checkbox, Box, useDisclosure,
} from '@chakra-ui/react'
import { Link, useSearchParams } from 'react-router-dom';
import { SearchBar } from '../common/SearchBar';
import ActionButton from '../common/ActionButton';
import SPagination from '../common/SPagination';
import SMenuFilter from '../common/SMenuFilter';
import useDeleteProgram from 'src/hooks/ManageProgram/useDeleteProgram';
import { 레벨번역, 영역번역} from "../../common/util";
import SModal from '../common/SModal';
import useSearchProgram from 'src/hooks/ManageProgram/useSearchProgram';
import SToast from '../common/SToast';
import * as XLSX from 'xlsx';
import { Program } from 'src/hooks/ManageProgram/useSearchProgram';

export default function ProgramList() {
    const { deleteProgram } = useDeleteProgram();
    const { searchProgram } = useSearchProgram();
    const [programSearchResult, setProgramSearchResult] = useState<Program | null>();
    const { errorToast, successToast, customErrorToast } = SToast();

    // const [page, setPage] = useState<number>(1); 

    const [searchParams, setSearchParams] = useSearchParams();
    const [page, setPage] = useState<number>(parseInt(searchParams.get('page') || '1'));

    const 샘플필터 = ['상급','중급','초급']

    const [searchKeyword, setSearchKeyword] = useState('');
    const [checkedProgramIds, setCheckedProgramIds] = useState<number[]>([]);
    const [allChecked, setAllChecked] = useState(false);
    const { 
        isOpen: isOpenDeleteModal, 
        onOpen: onOpenDeleteModal, 
        onClose: onCloseDeleteModal
    } = useDisclosure();

    const { 
        isOpen: isOpenDownloadModal, 
        onOpen: onOpenDownloadModal, 
        onClose: onCloseDownloadModal
    } = useDisclosure();

    const updateSearchParams = (newPage: number, newKeyword: string) => {
        const params: { page?: string; keyword?: string } = {};
        if (newPage !== 1) params.page = newPage.toString();
        if (newKeyword) params.keyword = newKeyword;
        setSearchParams(params);
    };

    const reloadList = () => {
        searchProgram(searchKeyword, page)
            .then((result)=>{
                setProgramSearchResult(result)
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const handlePageChange = (selectedPage: number) => {
        setPage(selectedPage);
        setCheckedProgramIds([]);
        setAllChecked(false);
        updateSearchParams(selectedPage, searchKeyword);
    };

    const handleAllCheckboxChange = (e: { target: { checked: any; }; }) => {
        const isChecked = e.target.checked;
        setAllChecked(isChecked);

        if (isChecked) {
          const allProgramIds = programSearchResult?.content.map((program) => program.programId) || [];
          setCheckedProgramIds(allProgramIds);
        } else {
          setCheckedProgramIds([]);
        }
    };

    const handleCheckboxChange = (programId: number) => {       
        const updatedCheckedProgramIds = [...checkedProgramIds];
    
        if (updatedCheckedProgramIds.includes(programId)) {
          const index = updatedCheckedProgramIds.indexOf(programId);
          if (index !== -1) {
            updatedCheckedProgramIds.splice(index, 1);
          }
        } else {
          updatedCheckedProgramIds.push(programId);
        }
      
        const allProgramIds = programSearchResult?.content.map((program) => program.programId) || [];
        setAllChecked(updatedCheckedProgramIds.length === allProgramIds.length);
        setCheckedProgramIds(updatedCheckedProgramIds);
    };

    const onDeleteProgram = async () => {
        if (checkedProgramIds.length === 0) {
            customErrorToast('삭제할 프로그램을 선택해주세요.')
        } else {
            let deleteCount = 0;

            for (const programId of checkedProgramIds) {
                const deleteCall = await deleteProgram(programId);
                if (deleteCall === 200) {
                    deleteCount++;
                } else {
                    errorToast();
                }
            }

            if (deleteCount > 0) {
                successToast(`${deleteCount}개 항목이 성공적으로 삭제 됐습니다.`);
                reloadList();
                setCheckedProgramIds([]);
                setAllChecked(false);
            }
        }
    };

    const onSearch = async (params:string) => {
        setPage(1)
        updateSearchParams(1, searchKeyword);
        reloadList();
    }

    const downloadExel = (data: any) => {
        const transformedData = data.map((a: any, i: any) => {
            return {
                '순서': i + 1,
                '레벨': 레벨번역(a.programLevel),
                '세부 레벨': a.detailLevel,
                '영역': 영역번역(a.programArea),
                '프로그램 이름': a.programName,
            };
        });
        const ws = XLSX.utils.json_to_sheet(transformedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        XLSX.writeFile(wb, '전체 프로그램 목록.xlsx');
    }

    const onDownloadExel = async () => {
        const totalCall = await searchProgram(searchKeyword, 1, programSearchResult?.totalElements);
        downloadExel(totalCall.content)
    }

    useEffect(()=>{
        const pageFromUrl = parseInt(searchParams.get('page') || '1');
        setPage(pageFromUrl);
        reloadList();
    },[page])
    
    return(
        <>
        <Container>
            <Title>프로그램 관리</Title>
            <SubContainer>
                <SubTitle>프로그램 목록</SubTitle>
                <Link to={'/manage-program/create'}>
                    <ActionButton 
                        backgroundColor={colors.button.blue} 
                        _hover={{bg: "hover.blue", color:'white'}} 
                        text='프로그램 등록' 
                    />
                </Link>
                <ActionButton
                    backgroundColor={colors.button.black} 
                    _hover={{bg: "hover.black", color:'white'}} 
                    onClick={onOpenDownloadModal}
                    text='전체 프로그램 다운' 
                />
                <ActionButton
                    backgroundColor={colors.button.red} 
                    _hover={{bg: "hover.red", color:'white'}} 
                    onClick={onOpenDeleteModal}
                    text='선택 항목 삭제' 
                />
                <SModal 
                    title='삭제하기'
                    desc='정말로 삭제하시겠습니까?'
                    onClick={onDeleteProgram} 
                    isOpen={isOpenDeleteModal} 
                    onOpen={onOpenDeleteModal} 
                    onClose={onCloseDeleteModal} 
                />
                <SModal 
                    title='전체 프로그램 다운로드하기'
                    desc={`${programSearchResult?.totalElements}개의 프로그램이 다운로드 됩니다.`}
                    color='black'
                    onClick={onDownloadExel} 
                    isOpen={isOpenDownloadModal} 
                    onOpen={onOpenDownloadModal} 
                    onClose={onCloseDownloadModal} 
                />
                
            </SubContainer>
            <TableContainer height='100%'>
            <Box borderRadius="8px" overflow="hidden" borderLeft='1px' borderRight='1px' borderColor='brand.grey'>
                <Table variant='simple' size='md'>
                    <Thead backgroundColor='brand.grey'>
                        <Tr>
                            <Th textAlign="center" width='7%' height='61px' borderRight='1px' borderBottom='none' borderColor='brand.labelGrey' verticalAlign='middle'>
                                <Checkbox
                                    isChecked={allChecked}
                                    onChange={handleAllCheckboxChange}
                                    borderColor='brand.navy'
                                />
                            </Th>
                            <Th width='7%' textAlign="center" verticalAlign='middle' borderRight='1px' borderColor='brand.labelGrey' borderBottom='none' fontSize='18px' fontFamily='KFAGothic'>순서</Th>
                            <Th width='14%' textAlign="center" verticalAlign='middle' borderRight='1px' borderColor='brand.labelGrey' borderBottom='none'>
                                <SMenuFilter title='레벨' items={샘플필터}/>    
                            </Th>
                            <Th width='14%' textAlign="center" verticalAlign='middle' borderRight='1px' borderColor='brand.labelGrey' borderBottom='none'>
                                <SMenuFilter title='세부 레벨' items={샘플필터}/>
                            </Th>
                            <Th width='14%' textAlign="center" verticalAlign='middle' borderRight='1px' borderColor='brand.labelGrey' borderBottom='none'>
                                <SMenuFilter title='영역' items={샘플필터}/>
                            </Th>
                            <Th width='44%' textAlign="center" verticalAlign='middle' fontSize='18px' fontFamily='KFAGothic'>프로그램 이름</Th>
                        </Tr>
                    </Thead>
                    <Tbody fontSize='16px'>
                    {programSearchResult?.content.map((a,i)=>{
                        const totalItems = programSearchResult.totalElements || 0;
                        const orderNumber = totalItems - ((page - 1) * 8 + i);
                        return (
                        <Tr key={a.programId}>
                            <Td textAlign="center" verticalAlign='middle' borderBottom='1px' borderRight='1px' borderColor='brand.grey' height='72px'>
                                <Checkbox
                                    isChecked={checkedProgramIds.includes(a.programId)}
                                    onChange={() => { handleCheckboxChange(a.programId); }}
                                    borderColor='brand.navy'
                                />
                            </Td>
                            <Td textAlign="center" verticalAlign='middle' borderBottom='1px' borderRight='1px' borderColor='brand.grey'>{orderNumber}</Td>
                            <Td textAlign="center" verticalAlign='middle' borderBottom='1px' borderRight='1px' borderColor='brand.grey'>{레벨번역(a.programLevel)}</Td>
                            <Td textAlign="center" verticalAlign='middle' borderBottom='1px' borderRight='1px' borderColor='brand.grey'>{a.detailLevel}</Td>
                            <Td textAlign="center" verticalAlign='middle' borderBottom='1px' borderRight='1px' borderColor='brand.grey'>{영역번역(a.programArea)}</Td>
                            <Td textAlign="center" verticalAlign='middle' borderBottom='1px' borderColor='brand.grey' textDecoration='underline' textUnderlineOffset='4px'>
                                <Link to={`/manage-program/${a.programId}`}>{a.programName}</Link>
                            </Td>
                        </Tr>
                        )
                    })}
                    </Tbody>
                </Table>
                {programSearchResult && programSearchResult.totalElements < 1 ? (
                <NoResult>
                    검색 결과가 없습니다
                </NoResult>
                ) : null}
            </Box>
            </TableContainer>
            <BottomContainer>
                <SearchBar 
                    placeholder='프로그램명 검색' 
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onEnterPress={(e) => {
                        if (e.key === "Enter") {
                          onSearch(searchKeyword);
                        }
                    }}
                />
                <SPagination 
                    activePage={page} 
                    totalItemsCount={programSearchResult?.totalElements}
                    handlePageChange={handlePageChange}
                />
            </BottomContainer>
        </Container>
        </>
    )
}

const Container = styled.div`
width: calc(100vw - 506px);
min-width: 850px;
`

const Title = styled.div`
color: ${colors.black};
font-family: 'KFAGothicBold';
font-size: 22px;
margin-bottom: 41px;
`

const SubContainer = styled.div`
display: flex;
button {
    margin-left: 12px;
    margin-bottom: 15px;
}
`

const SubTitle = styled.span`
color: ${colors.black};
font-size: 18px;
line-height: 40px;
margin-right: auto;
`

const BottomContainer = styled.div`
display: flex;
justify-content: space-between;
margin-top: 43px;
margin-bottom: 51px;
`

const NoResult = styled.div`
width: 100%;
text-align: center;
padding-top: 287px;
height: 577px;
border-bottom: 1px solid ${colors.brandGrey};
color: ${colors.grey};
`;